import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Download, CheckCircle, Calendar, Trophy, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistrationCard = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchRegistration();
  }, []);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(`${API}/registrations/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setRegistration(response.data);
    } catch (error) {
      toast.error('Erro ao buscar inscrição');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const element = document.getElementById('registration-card-download');
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#050505',
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      const link = document.createElement('a');
      link.download = `challenge-run-inscricao-${registration.full_name.replace(/\s/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Cartão baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar:', error);
      toast.error('Erro ao baixar o cartão');
    } finally {
      setDownloading(false);
    }
  };

  const teamColors = {
    'Time Verde': '#C5FF00',
    'Time Preto': '#1A1A1A',
    'Time Branco': '#FFFFFF'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl font-unbounded">Carregando...</div>
      </div>
    );
  }

  if (registration?.payment_status !== 'pago') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="font-unbounded font-bold text-2xl mb-4">Pagamento Pendente</h2>
          <p className="text-muted-foreground mb-6">
            Seu pagamento ainda está sendo processado. Após a confirmação, você receberá seu cartão de inscrição.
          </p>
          <Button onClick={() => navigate('/')} className="bg-primary text-black">
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Registration Card for Download */}
        <div id="registration-card-download" className="bg-[#050505] p-12 mb-8" style={{ width: '1080px', margin: '0 auto' }}>
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-primary rounded-full mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-unbounded font-black text-5xl tracking-tight uppercase mb-2">
              <span className="text-white">INSCRIÇÃO </span>
              <span className="text-primary">CONFIRMADA!</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Parabéns! Você está oficialmente inscrito no Challenge Run 6.0
            </p>
          </div>

          {/* Main Card */}
          <div className="border border-white/10 p-10">
            {/* Logo and Title */}
            <div className="text-center mb-10 pb-8 border-b border-white/10">
              <img 
                src="https://customer-assets.emergentagent.com/job_71e90c7e-0627-431e-aeed-455d200cb65d/artifacts/oqvzxieq_R77%20logo.PNG"
                alt="R77 Logo"
                className="h-20 mx-auto mb-6"
              />
              <h2 className="font-unbounded font-black text-4xl tracking-tight uppercase text-white mb-3">
                CHALLENGE RUN 6.0
              </h2>
              <p className="text-primary font-mono text-base uppercase tracking-widest">
                CARTÃO DE INSCRIÇÃO
              </p>
            </div>

            {/* Participant Info Grid */}
            <div className="grid grid-cols-2 gap-x-12 gap-y-8 mb-10">
              {/* Nome Completo */}
              <div>
                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider block mb-2">
                  NOME COMPLETO
                </label>
                <p className="text-white text-xl font-semibold">{registration.full_name}</p>
              </div>

              {/* Telefone */}
              <div>
                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider block mb-2">
                  TELEFONE
                </label>
                <p className="text-white text-xl font-mono">{registration.phone}</p>
              </div>

              {/* Time */}
              <div>
                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider block mb-2">
                  TIME
                </label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full"
                    style={{ 
                      backgroundColor: teamColors[registration.team],
                      border: registration.team === 'Time Branco' ? '2px solid rgba(197, 255, 0, 0.5)' : 'none'
                    }}
                  />
                  <p className="text-white text-xl font-semibold">{registration.team}</p>
                </div>
              </div>

              {/* Nível */}
              <div>
                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider block mb-2">
                  NÍVEL
                </label>
                <p className="text-white text-xl font-semibold">{registration.level}</p>
              </div>

              {/* Camisa */}
              <div>
                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider block mb-2">
                  CAMISA
                </label>
                <p className="text-white text-xl">
                  {registration.with_shirt ? `Sim - Tamanho ${registration.shirt_size}` : 'Não'}
                </p>
              </div>

              {/* Valor Pago */}
              <div>
                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider block mb-2">
                  VALOR PAGO
                </label>
                <p className="text-primary text-2xl font-bold">R$ {registration.amount.toFixed(2)}</p>
              </div>
            </div>

            {/* Event Info Box */}
            <div className="bg-primary/10 border border-primary/30 p-8 mb-8">
              <h3 className="text-primary font-unbounded font-bold text-xl mb-6 uppercase">
                Informações do Evento
              </h3>
              <div className="grid grid-cols-3 gap-8">
                <div className="flex flex-col items-center text-center">
                  <Calendar className="w-8 h-8 text-primary mb-3" />
                  <p className="text-muted-foreground text-xs mb-1">Duração</p>
                  <p className="text-white font-bold">21 dias</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Trophy className="w-8 h-8 text-primary mb-3" />
                  <p className="text-muted-foreground text-xs mb-1">Premiação</p>
                  <p className="text-white font-bold">Até 5º lugar</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <Users className="w-8 h-8 text-primary mb-3" />
                  <p className="text-muted-foreground text-xs mb-1">Sunset Run</p>
                  <p className="text-white font-bold">Incluído</p>
                </div>
              </div>
            </div>

            {/* Registration ID */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground font-mono">
                ID da Inscrição: {registration.id}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 px-8 py-6 text-base"
          >
            <Download className="w-5 h-5 mr-2" />
            {downloading ? 'Baixando...' : 'Baixar Cartão'}
          </Button>
          <Button
            data-testid="card-ranking-btn"
            onClick={() => navigate('/ranking')}
            className="bg-primary/20 border border-primary text-primary hover:bg-primary/30 font-unbounded uppercase tracking-wider px-8 py-6 text-base"
          >
            <Trophy className="w-5 h-5 mr-2" />
            Ver Ranking
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-white/20 hover:border-primary hover:text-primary font-unbounded uppercase tracking-wider px-8 py-6 text-base"
          >
            Voltar ao Início
          </Button>
        </div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <h3 className="font-unbounded font-bold text-2xl mb-6">Próximos Passos</h3>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <div className="bg-card border border-white/10 p-6">
              <div className="text-primary font-bold text-2xl mb-2">1</div>
              <h4 className="font-bold mb-2">Guarde seu Cartão</h4>
              <p className="text-sm text-muted-foreground">
                Faça o download e mantenha salvo para apresentar no evento
              </p>
            </div>
            <div className="bg-card border border-white/10 p-6">
              <div className="text-primary font-bold text-2xl mb-2">2</div>
              <h4 className="font-bold mb-2">Entre no Grupo</h4>
              <p className="text-sm text-muted-foreground">
                Aguarde instruções para entrar no grupo do seu time
              </p>
            </div>
            <div className="bg-card border border-white/10 p-6">
              <div className="text-primary font-bold text-2xl mb-2">3</div>
              <h4 className="font-bold mb-2">Prepare-se</h4>
              <p className="text-sm text-muted-foreground">
                Comece seu treino e se prepare para os 21 dias de desafio!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RegistrationCard;
